import { Button, message, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { AppContext, RefreshStatus } from '../../../../App';
import { revokeConsentAPI } from '../../../../Services/PFMClient/DashboardService';
import { logoBaseURL } from '../../../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../../../Utils/eventUtils';
import { getAltJourney } from '../../../../Utils/storageUtils';
import { altJourneyType } from '../../../../Utils/types';
import { formatDates, formatToINR } from '../../../../Utils/utils';
import { getImagePath } from '../../../../Utils/assetUtils';
import sibLogo from '../../../../Assets/sib/sib_logo.webp';
import AllTransactions from '../../AllTransactions';
import SIBErrorScreen from '../../Common/ErrorScreen';
import styles from './index.module.css';
import { ReactComponent as Down } from '../../../../Assets/yesbank/down-arrow.svg';
import { ReactComponent as Up } from '../../../../Assets/yesbank/up-arrow.svg';

const BankSummary: React.FC<{
  index: number;
  accounts: any;
  onRevokeSuccess: () => void;
  onBack: () => void;
}> = ({ accounts, index, onRevokeSuccess, onBack }) => {
  const {
    statusData,
    setAnalyticsStatus,
    setIsAccountDetailsFetched,
    setStatusPolling,
    dataRefreshStatus,
    isAmountVisible,
    setBackButtonHandler,
  }: any = useContext(AppContext);

  // Order the account chips by fiType: DEPOSIT first, then TERM_DEPOSIT (FD),
  // then RECURRING_DEPOSIT (RD); anything else after. Computed before
  // `selectedAcconts` so its initial value already matches the sorted list -
  // otherwise the first render seeds it from the raw, unsorted `accounts[index]`
  // order, which briefly fetches transactions for the wrong (unsorted) account
  // until the sync effect below corrects it.
  const accountFilter = useMemo(() => {
    const fiTypeOrder: Record<string, number> = {
      DEPOSIT: 0,
      TERM_DEPOSIT: 1,
      'TERM-DEPOSIT': 1,
      RECURRING_DEPOSIT: 2,
    };
    const filtered =
      accounts?.[index]?.filter(
        (acc: any) =>
          statusData?.current?.accountDetails?.filter((accountDetail: any) => {
            return (
              accountDetail.linkRefNumber === acc.linkedAccRef &&
              accountDetail.status === 'REVOKE_INITIATED'
            );
          }).length === 0,
      ) ?? [];
    return [...filtered].sort(
      (a: any, b: any) =>
        (fiTypeOrder[a.fiType] ?? 99) - (fiTypeOrder[b.fiType] ?? 99),
    );
  }, [accounts, index]);

  const [selectedAcconts, setSelectedAccounts] = useState<any>(
    accountFilter?.[0] || {},
  );
  const [counter, setCounter] = useState(0);
  const [revokeConsentSheet, setRevokeConsentSheet] = useState(false);
  const [errorConsentRevoke, setErrorConsentRevoke] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState(undefined);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const refIdAccountMapping = useMemo(() => {
    var localMap: any = {};

    statusData?.current?.accountDetails?.forEach((accountDetail: any) => {
      localMap = {
        ...localMap,
        [accountDetail.referenceId]: localMap[accountDetail.referenceId]
          ? localMap[accountDetail.referenceId].concat(accountDetail)
          : [accountDetail],
      };
    });

    return localMap;
  }, []);

  const isConsentValid = useMemo(() => {
    return (
      selectedAcconts?.consentStart != null &&
      selectedAcconts?.consentExpiry != null &&
      selectedAcconts?.consentStart !== '' &&
      selectedAcconts?.consentExpiry !== ''
    );
  }, [accounts]);

  const revokeConsent = () => {
    setIsButtonLoading(true);
    revokeConsentAPI({ referenceId: selectedRefId! })
      .then((_res) => {
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setStatusPolling((prev: any) => prev + 1);
        onRevokeSuccess();
      })
      .catch((_e) => {
        setErrorConsentRevoke(true);
      })
      .finally(() => {
        setRevokeConsentSheet(false);
        setIsButtonLoading(false);
      });
  };
  const updatedSelectedAccounts = (acc: any) => {
    setSelectedAccounts(acc);
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const goTo = (idx: number) => {
    if (scrollRef.current) {
      const slideWidth = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({
        left: idx * slideWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (index !== null && index !== undefined) {
      goTo(index);
      document.getElementById(index.toString())?.scrollIntoView();
    }
  }, [index]);


  useEffect(() => {
    console.log('[BackButtonHandler] registering handler BankSummary', {
      revokeConsentSheet,
    });
    setBackButtonHandler(() => {
      return () => {
        if (revokeConsentSheet) {
          console.log(
            '[BackButtonHandler] BankSummary fired: closing revoke consent sheet in BankSummary',
          );
          setRevokeConsentSheet(false);
        } else {
          console.log(
            '[BackButtonHandler] BankSummary fired: navigating back from BankSummary',
          );
          onBack();
        }
      };
    });
  }, [revokeConsentSheet]);

  const handleCarouselScroll = () => {
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      if (scrollRef.current) {
        const idx = Math.round(
          scrollRef.current.scrollLeft / scrollRef.current.offsetWidth,
        );
        setCounter(idx);
        if (accountFilter?.length) {
          updatedSelectedAccounts(
            accountFilter[idx % accountFilter.length],
          );
        }
        document.getElementById(idx.toString())?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }, 150);
  };

  const handleImageError = (event: any) => {
    event.target.src = getImagePath('images/bank.svg');
  };

  // Keep selectedAcconts in sync with the currently focused chip/slide. Uses
  // accountFilter (the sorted, revoke-filtered list) so counter indexes the
  // same order the chips and carousel render in — the first chip (DEPOSIT) is
  // selected by default (counter starts at 0).
  useEffect(() => {
    const updated = accountFilter?.[counter];
    if (updated !== undefined) {
      setSelectedAccounts(updated);
    }
  }, [accountFilter, counter]);

  function convertConstantToTitle(str: string) {
    return str
      .toLowerCase() // convert the entire string to lower case
      .split('_') // split the string by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize the first letter of each word
      .join(' '); // join the words back with a space
  }

  // Short account-type tag shown on the chip: SB (savings/deposit), FD (fixed
  // deposit), RD (recurring deposit).
  const getShortAccountType = (acc: any) => {
    const fiType = (acc?.fiType || '').toUpperCase();
    if (fiType === 'TERM_DEPOSIT' || fiType === 'TERM-DEPOSIT') return 'FD';
    if (fiType === 'RECURRING_DEPOSIT') return 'RD';
    return 'SB';
  };
  const [showAll, setShowAll] = useState(false);
  const isYesOnlineJourney = getAltJourney() === altJourneyType.YESONLINE;

  return (
    <div className={styles.bankSummaryContainer}>
      {/* Header Card */}
      <div className={styles.headerCard}>
        <div className={styles.bankHeader}>
          <div className={styles.bankLogoWrapper}>
            <img
              src={
                String(index).split('$')[0] === 'SIB'
                  ? sibLogo
                  : `${logoBaseURL}${String(index).split('$')[0]}.jpeg`
              }
              alt={accounts?.[index]?.[0]?.fipName}
              className={styles.bankLogoImg}
              onError={handleImageError}
            />
          </div>
          <Typography.Text className={styles.bankNameTitle}>
            {accounts?.[index]?.[0]?.fipName}
          </Typography.Text>
        </div>

        <Typography.Text className={styles.balanceLabel}>
          ACCOUNT BALANCE
        </Typography.Text>

        <div className={styles.headerBalanceContainer}>
          <div className={styles.dashedDivider} />
          <Typography.Text className={styles.headerBalanceValue}>
            {formatToINR(selectedAcconts.balance, isAmountVisible)}
          </Typography.Text>
          <div className={styles.dashedDivider} />
        </div>

        {selectedAcconts.date ? (
          <Typography.Text className={styles.dateInfo}>
            as on {dayjs(selectedAcconts.date).format("Do MMM' YYYY")}
          </Typography.Text>
        ) : (
          selectedAcconts.dataFailed && (
            <Typography.Text className={styles.dateInfo}>
              Unable to fetch latest data from your bank
            </Typography.Text>
          )
        )}

        {accountFilter?.length > 1 && (
          <div
            className={
              accountFilter?.length > 3
                ? styles.accountsTag
                : styles.accountsTagCenter
            }
          >
            {accountFilter?.map((acc: any, idx: number) => (
              <div
                id={idx.toString()}
                key={idx}
                className={`${styles.accounts} yourBanksAccounts ${
                  acc.accNo === selectedAcconts.accNo
                    ? styles.selectedAcconts
                    : ''
                }`}
                onClick={() => {
                  if (acc.accNo !== selectedAcconts.accNo) {
                    setCounter(idx);
                    updatedSelectedAccounts(acc);
                    goTo(idx);
                  }
                }}
              >
                <span className={styles.accountTypeTag}>
                  {getShortAccountType(acc)}
                </span>
                <Typography.Text
                  className={`${styles.accountsText} ${
                    acc.accNo === selectedAcconts.accNo
                      ? styles.selectedAccontsText
                      : ''
                  }`}
                >
                  XXXX {acc.accNo.slice(-4)}
                </Typography.Text>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.carouselWrapper}>
        <div
          ref={scrollRef}
          className={styles.carouselTrack}
          onScroll={handleCarouselScroll}
        >
          {accountFilter.map((acc: any) => (
            <div className={styles.detailsSlide} id={acc.id}>
              <div className={styles.slideContent}>
                {selectedAcconts.accNo && (
                  <div
                    className={`${styles.accountDetails} ${
                      isYesOnlineJourney ? styles.yesOnlineBorder : ''
                    }`}
                  >
                    <Typography.Text className={styles.detailsHeading}>
                      Account Details
                    </Typography.Text>
                    {selectedAcconts.holderName && (
                      <div className={styles.accountDetaisListItemFirst}>
                        <Typography.Text className={styles.listItemHeader}>
                          ACCOUNT HOLDER
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {selectedAcconts.holderName}
                        </Typography.Text>
                      </div>
                    )}

                    <div className={styles.accountDetaisListItem}>
                      <Typography.Text className={styles.listItemHeader}>
                        ACCOUNT NUMBER
                      </Typography.Text>
                      <Typography.Text className={styles.listItemValue}>
                        XXXX {selectedAcconts.accNo.slice(-4)}
                      </Typography.Text>
                    </div>
                    {selectedAcconts.ifscCode && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          IFSC CODE
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {selectedAcconts.ifscCode}
                        </Typography.Text>
                      </div>
                    )}

                    {selectedAcconts.interestRate && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          INTEREST RATE
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {parseInt(selectedAcconts.interestRate).toFixed(2)} %
                        </Typography.Text>
                      </div>
                    )}

                    {selectedAcconts.maturityAmount && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          MATURITY AMOUNT
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {formatToINR(selectedAcconts?.maturityAmount)}
                        </Typography.Text>
                      </div>
                    )}

                    {selectedAcconts.maturityDate && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          MATURITY DATE
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {dayjs(selectedAcconts.maturityDate).format(
                            'DD-MM-YYYY',
                          )}
                        </Typography.Text>
                      </div>
                    )}

                    {selectedAcconts.interestPayout && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          INTEREST PAYOUT
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {convertConstantToTitle(
                            selectedAcconts.interestPayout,
                          )}
                        </Typography.Text>
                      </div>
                    )}

                    {selectedAcconts.holderEmail && (
                      <div className={styles.accountDetaisListItem}>
                        <Typography.Text className={styles.listItemHeader}>
                          EMAIL ADDRESS
                        </Typography.Text>
                        <Typography.Text className={styles.listItemValue}>
                          {selectedAcconts.holderEmail}
                        </Typography.Text>
                      </div>
                    )}
                    <div className={styles.accountDetaisListItem}>
                      <Typography.Text className={styles.listItemHeader}>
                        ACCOUNT TYPE
                      </Typography.Text>
                      <Typography.Text className={styles.listItemValue}>
                        {selectedAcconts.accType}
                      </Typography.Text>
                    </div>

                    {showAll &&
                    selectedAcconts.fipId?.split('$')[0] !== 'SIB' ? (
                      <>
                        <div className={styles.accountDetaisListItem}>
                          <Typography.Text className={styles.listItemHeader}>
                            CONSENT GIVEN ON
                          </Typography.Text>
                          <Typography.Text className={styles.listItemValue}>
                            {formatDates(selectedAcconts?.consentStart)}
                          </Typography.Text>
                        </div>
                        <div className={styles.accountDetaisListItem}>
                          <Typography.Text className={styles.listItemHeader}>
                            CONSENT EXPIRY
                          </Typography.Text>
                          <Typography.Text className={styles.listItemValue}>
                            {formatDates(selectedAcconts?.consentExpiry)}
                          </Typography.Text>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                    {selectedAcconts.fipId?.split('$')[0] !== 'SIB' &&
                      isConsentValid && (
                      <>
                        <Typography.Link
                          onClick={() => {
                            setShowAll(!showAll);
                          }}
                          className={styles.consentToggleLink}
                        >
                          {showAll ? (
                            <>
                              <Up color='var(--revoke-button)' /> Hide Consent
                              Details
                            </>
                          ) : (
                            <>
                              <Down color='var(--revoke-button)' /> Show Consent
                              Details
                            </>
                          )}
                        </Typography.Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <Sheet
          isOpen={revokeConsentSheet}
          detent='content-height'
          className='revokeConsentSheet'
          onClose={() => {
            setRevokeConsentSheet(false);
          }}
        >
          <Sheet.Backdrop
            onTap={() => setRevokeConsentSheet(false)}
          ></Sheet.Backdrop>

          <Sheet.Container>
            <div className={styles.consentRevokeSheetContainer}>
              <div className={styles.closeIconContainer}>
                <div
                  className={styles.closeIcon}
                  onClick={() => {
                    setRevokeConsentSheet(false);
                  }}
                >
                  <Typography.Text className={styles.closeText}>
                    X
                  </Typography.Text>
                </div>
              </div>

              <Typography.Text className={styles.consentRevokeSheetTitle}>
                Do you really want to revoke the consent?
              </Typography.Text>
              <Typography.Text className={styles.revokeSubtitle}>
                On revoking, you will not be able to get insights for the below
                account(s).
              </Typography.Text>
              <div className={styles.revokeAccountsWrapper}>
                <div className={styles.fipRow}>
                  <div className={styles.iconAsset}>
                    <img
                      src={
                        selectedAcconts.fipId.split('_')[0] === 'SIB'
                          ? sibLogo
                          : `https://logo.ignosis.ai/${selectedAcconts.fipId.split('_')[0]}.jpeg`
                      }
                      className={styles.fipLogoImg}
                      onError={handleImageError}
                    />
                  </div>
                  <Typography.Text className={styles.fipTitle}>
                    {selectedAcconts.fipName}
                  </Typography.Text>
                </div>

                <Typography.Text className={styles.accountsLinkedTitle}>
                  Accounts linked with this consent:
                </Typography.Text>
                <div className={styles.accountList}>
                  {refIdAccountMapping[selectedRefId!]?.map(
                    (bankDetails: any) => {
                      return (
                        <div className={styles.revokeAccountItem}>
                          <Typography.Text className={styles.fipTitle}>
                            {bankDetails.fipName}
                          </Typography.Text>
                          <Typography.Text className={styles.accountNumberText}>
                            XXXX {bankDetails.maskedAccountNumber.slice(-4)}
                          </Typography.Text>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
              <div className={styles.consentRevokeButtonContainer}>
                <Button
                  className={styles.consentRevokeGoBack}
                  onClick={() => {
                    pushEventToParent({
                      eventCode: EVENTCODES.GO_BACK_CONSENT_REVOKE,
                    });
                    setRevokeConsentSheet(false);
                  }}
                >
                  <Typography.Text className={styles.goBackText}>
                    Go Back
                  </Typography.Text>
                </Button>
                <Button
                  loading={isButtonLoading}
                  className={styles.consentRevokeConfirm}
                  onClick={() => {
                    if (dataRefreshStatus === RefreshStatus.IN_PROGRESS) {
                      message.info(
                        <Typography.Text className={styles.infoAlertText}>
                          Your account data refresh is in progress. Please try
                          again in some time.
                        </Typography.Text>,
                      );
                    } else {
                      pushEventToParent({
                        eventCode: EVENTCODES.CONSENT_REVOKE_CLICKED,
                      });
                      revokeConsent();
                    }
                  }}
                >
                  <Typography.Text className={styles.revokeButtonText}>
                    Revoke
                  </Typography.Text>
                </Button>
              </div>
            </div>
          </Sheet.Container>
        </Sheet>

        {errorConsentRevoke && (
          <SIBErrorScreen
            message='We couldn’t revoke your consent at this time. Your existing consent remains unchanged, and data remains protected. Please try again.'
            buttonText='Okay'
            onButtonClick={() => {
              pushEventToParent({
                eventCode: EVENTCODES.REVOKE_CONSENT_FAILED,
              });
              setErrorConsentRevoke(false);
            }}
          />
        )}

        <div className={styles.transactionsCard}>
          <AllTransactions
            isViewTransationVisible
            key={counter}
            selectedAccounts={selectedAcconts}
          >
            {!selectedAcconts.fipId?.startsWith('SIB') && (
              <div className={styles.revokeConsentContainer}>
                <button
                  className={styles.stopTrackingButton}
                  onClick={() => {
                    pushEventToParent({
                      eventCode: EVENTCODES.REVOKE_YOUR_CONSENT_CLICKED,
                    });
                    setRevokeConsentSheet(true);
                    setSelectedRefId(
                      selectedAcconts.consentReferenceId ??
                        selectedAcconts.referenceId,
                    );
                  }}
                >
                  <span className={styles.revokeConsentButtonText}>
                    Stop Tracking
                  </span>
                </button>
              </div>
            )}
          </AllTransactions>
        </div>
      </div>
    </div>
  );
};

export default BankSummary;
